import type {
	IssueAttachmentModel,
	PortalIssueAttachmentUploadResponseDto,
	PortalIssueConfirmResolutionRequestDto,
	PortalIssueConfirmResolutionResponseDto,
	PortalIssueDetailDto,
	PortalIssueListResponseDto,
	PortalIssueReplyRequestDto,
	PortalIssueReplyResponseDto,
	PortalSubmitIssueRequestDto,
	PortalSubmitIssueResponseDto,
} from '@supply-mover-2-0/shared';
import { portalFetch, portalFetchMultipart } from '../lib/apiClient';

/**
 * Submit a support issue from the storefront (PD-13705). Login required —
 * the API links the issue to the customer's active client.
 */
export async function submitPortalIssue(
	token: string,
	payload: PortalSubmitIssueRequestDto,
): Promise<PortalSubmitIssueResponseDto> {
	return portalFetch<PortalSubmitIssueResponseDto>('/issues', token, {
		method: 'POST',
		body: JSON.stringify(payload),
	});
}

/**
 * The customer's own submitted issues (PD-13773) — the API scopes the
 * list strictly to issues the signed-in portal user created.
 */
export async function listPortalIssues(
	token: string,
): Promise<PortalIssueListResponseDto> {
	return portalFetch<PortalIssueListResponseDto>('/issues', token);
}

/** Issue detail + communication thread (PD-13773). */
export async function getPortalIssue(
	token: string,
	issueId: number,
): Promise<PortalIssueDetailDto> {
	return portalFetch<PortalIssueDetailDto>(`/issues/${issueId}`, token);
}

/** Append a customer reply to the thread (PD-13773). */
export async function replyToPortalIssue(
	token: string,
	issueId: number,
	payload: PortalIssueReplyRequestDto,
): Promise<PortalIssueReplyResponseDto> {
	return portalFetch<PortalIssueReplyResponseDto>(
		`/issues/${issueId}/messages`,
		token,
		{
			method: 'POST',
			body: JSON.stringify(payload),
		},
	);
}

/**
 * PD-16525 — answer "did that solve it?" on a request the team marked
 * Resolved: solved closes it, not solved hands it back to the team.
 */
export async function confirmPortalIssueResolution(
	token: string,
	issueId: number,
	payload: PortalIssueConfirmResolutionRequestDto,
): Promise<PortalIssueConfirmResolutionResponseDto> {
	return portalFetch<PortalIssueConfirmResolutionResponseDto>(
		`/issues/${issueId}/confirm-resolution`,
		token,
		{
			method: 'POST',
			body: JSON.stringify(payload),
		},
	);
}

/**
 * PD-16475 — upload one image to send with a new request or a reply. The
 * returned id goes in the message's `attachmentFileIds`.
 */
export async function uploadPortalIssueImage(
	token: string,
	file: File,
): Promise<IssueAttachmentModel> {
	const formData = new FormData();
	formData.append('file', file, file.name);

	const result =
		await portalFetchMultipart<PortalIssueAttachmentUploadResponseDto>(
			'/issues/attachments',
			token,
			formData,
		);

	return {
		fileId: result.fileId,
		name: result.name,
		contentType: result.contentType,
	};
}
